import React, { useCallback, useContext, useRef, useState } from 'react';
import styled from 'styled-components';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import useOnclickOutside from 'react-cool-onclickoutside';
import { redirect } from '@com/core/common/utils/url';
import { customerSVC } from '@com/core/common/services/api';
import { AppContext } from '@com/core/common/contexts/appContext';
import { MapContext } from '@com/core/common/contexts/mapContext';
import { DailyContext } from '@com/core/common/contexts/dailyContext';
import { reportError } from '@com/core/common/utils/sentry';
import { getMarketplaceConfig } from '@com/core/common/utils/config';
import { MARKETPLACE, CDN_IMG_BASE } from '@com/core/common/baseConfig';
import BlockButton from '../common/BlockButton';
import Loader from '../common/Loader';

type TActionModal = {
    error: string;
    screenType: string;
    actions: TAction[];
    misc?: {
        country_zone_code?: string;
    };
};

type TAction = {
    action: string;
    label: string;
    path?: string;
};

const NOTICATION_SENT_RESPONSE = {
    error: 'Thank you for your request. We’ll let you know when we start delivering in your area.',
    screenType: 'alert',
    actions: [
        { action: 'change-location', label: 'Change location' },
        { action: 'switch-noon', label: 'Switch to noon' },
    ],
};

const SearchBox = (): JSX.Element => {
    const config = getMarketplaceConfig(MARKETPLACE);

    const { locale } = useContext(AppContext);
    const { searchText, updateSearchText } = useContext(MapContext);
    const { changeCanEnterDaily, setBrowsingAddress } = useContext(DailyContext);

    const searchInput = useRef<HTMLInputElement>(null);

    const {
        clearSuggestions,
        ready,
        setValue,
        suggestions: { data, status },
        value,
    } = usePlacesAutocomplete();

    const [loadingLocation, setLoadingLocation] = useState(false);
    const [unserviceableLocationData, setUnserviceableLocationData] = useState<TActionModal | null>(null);

    // SEARCH STUFFS
    const ref = useOnclickOutside(() => {
        // When user clicks outside of the component, we can dismiss
        // the searched suggestions by calling this method
        clearSuggestions();
    });

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        // Update the keyword of the input element
        updateSearchText(null);
        setValue(e.target.value);
    };

    const handleSelect = ({ description }: { description: string }) => (): void => {
        // When user selects a place, we can replace the keyword without request data from API
        // by setting the second parameter as "false"
        updateSearchText(null);
        setValue(description, false);
        clearSuggestions();

        // Get latitude and longitude via utility functions
        getGeocode({ address: description })
            .then(results => getLatLng(results[0]))
            .then(({ lat, lng }) => {
                setLocationData(lat * config.map.precision, lng * config.map.precision);
            })
            .catch(error => {
                reportError(error);
            });
    };

    const setLocationData = useCallback(async (lat, lng) => {
        setLoadingLocation(true);
        try {
            const response = await customerSVC(locale.apiLocale).address.getDailyAreaFromLocation(lat, lng); // sets cookies
            changeCanEnterDaily(true);
            setBrowsingAddress(response.data);
            setLoadingLocation(false);
        } catch (err) {
            setUnserviceableLocationData(err);
            setLoadingLocation(false);
        }
    }, []);

    // LOCATION UNSERVICEABLE MODAL
    const handleAction = (action: TAction) => {
        switch (action.action) {
            case 'notify':
                requestNotifyService(action.path, { misc: unserviceableLocationData.misc } || {});
                break;
            case 'switch-noon':
                redirect(locale.isoLocale, '', 'www');
                break;
            default:
                setValue('');
                setUnserviceableLocationData(null);
                if (searchInput.current) {
                    searchInput.current.focus();
                }
        }
    };

    const requestNotifyService = useCallback(async (path, body) => {
        try {
            await customerSVC(locale.apiLocale).address.postDailyNotifyService(path, body);
            setUnserviceableLocationData(NOTICATION_SENT_RESPONSE);
        } catch (err) {
            if (err.status === 400) {
                setUnserviceableLocationData(NOTICATION_SENT_RESPONSE);
            } else {
                // todo error handling
                console.log(err);
            }
        }
    }, []);

    return (
        <div>
            <SearchBoxContainer ref={ref}>
                <div className="inputContainer">
                    <Input
                        data-qa="tf_searchFieldMaps"
                        placeholder="Search Your Location"
                        value={searchText || value}
                        onChange={handleInput}
                        disabled={!ready}
                        type="text"
                        ref={searchInput}
                    />
                    {loadingLocation ? (
                        <div className="helper">
                            <Loader color="brand" />
                        </div>
                    ) : (
                        <div className="helper">
                            {value.length > 0 ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setValue('');
                                        if (searchInput.current) {
                                            searchInput.current.focus();
                                        }
                                    }}>
                                    <img src={`${CDN_IMG_BASE}/simpleIcons/x-bodyLight.svg`} alt="Search" />
                                </button>
                            ) : (
                                <img src={`${CDN_IMG_BASE}/simpleIcons/search-bodyLight.svg`} alt="Search" />
                            )}
                        </div>
                    )}
                </div>

                {status === 'OK' && data && data.length ? (
                    <>
                        <SuggestionsContainer data-qa="suggestions_Maps">
                            {data.map(suggestion => (
                                <Suggestion key={suggestion.place_id} onClick={handleSelect(suggestion)}>
                                    {suggestion.structured_formatting.main_text}{' '}
                                    {suggestion.structured_formatting.secondary_text}
                                </Suggestion>
                            ))}
                        </SuggestionsContainer>
                    </>
                ) : null}
            </SearchBoxContainer>
            {unserviceableLocationData && unserviceableLocationData.screenType === 'alert' && (
                <Modal>
                    <div className="scrim" aria-hidden="true" />
                    <div className="content">
                        <UnserviceableContent>
                            <p>{unserviceableLocationData.error}</p>
                            <div className="actionsContainer">
                                {unserviceableLocationData.actions.map((action, index) => (
                                    <BlockButton
                                        onClick={() => handleAction(action)}
                                        key={action.action}
                                        variant={index !== 0 ? 'secondary' : null}>
                                        {action.label}
                                    </BlockButton>
                                ))}
                            </div>
                        </UnserviceableContent>
                    </div>
                </Modal>
            )}
        </div>
    );
};

const SearchBoxContainer = styled.div`
    position: relative;

    .inputContainer {
        position: relative;

        .helper {
            position: absolute;
            top: 0;
            ${({ theme: { right } }): string => `${right}: 0`};
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            ${({ theme: { right } }): string => `padding-${right}: 16px`};

            img {
                width: 20px;
                height: 20px;
            }
        }
    }
`;

const Input = styled.input`
    border-width: 0;
    padding: ${({ theme }): string => (theme.isRTL ? '14px 16px 14px 64px' : '14px 64px 14px 16px')};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.24);
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    background-color: ${({ theme }): string => theme.colors.bgPrimary};
`;

const SuggestionsContainer = styled.div`
    font-size: 0.9375rem;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.32);
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    z-index: 12;
    background-color: ${({ theme }): string => theme.colors.bgPrimary};
    position: absolute;
    width: 100%;
    margin-top: 4px;
    overflow: hidden;
`;

const Suggestion = styled.div`
    cursor: pointer;
    padding: 12px 16px;

    + div {
        border-top: 1px solid ${({ theme }): string => theme.colors.border};
    }

    &:hover {
        background-color: ${({ theme }): string => theme.colors.brandPrimaryLight};
    }
`;

const Modal = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    z-index: 60;
    display: flex;
    align-items: center;
    justify-content: center;

    .scrim {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(255, 255, 255, 0.5);
        backdrop-filter: blur(6px);
    }

    .content {
        background-color: ${({ theme }): string => theme.colors.bgPrimary};
        box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
        position: relative;
        width: 320px;
        max-height: 80%;
        overflow-y: auto;
        border-radius: 3px;
    }
`;

const UnserviceableContent = styled.div`
    padding: 28px 32px 32px;
    text-align: center;

    p {
        font-weight: 600;
    }

    .actionsContainer {
        margin-top: 28px;

        button + button {
            margin-top: 16px;
        }
    }
`;

export default SearchBox;
